<template>
  <div>
    <Head title="Hasil Verifikasi Permohonan Beasiswa" />
    <h1 class="mb-8 text-2xl font-bold">
      <Link class="text-indigo-400 hover:text-indigo-600" href="/verified-scholar-applications">
        Hasil Verifikasi
        Permohonan Beasiswa
      </Link>
      <span class="font-medium text-indigo-400">/</span>
      {{ application.nama_pemohon }}
    </h1>
    <div class="w-full mb-8 bg-white rounded-md shadow">
      <div class="w-full px-8 py-6 bg-gray-200">
        <div class="flex flex-col space-y-3 md:flex-row md:items-center md:justify-between md:space-y-0">
          <h2 class="text-xl">Informasi Permohonan Beasiswa ({{ application.jenis }})</h2>
          <div class="flex items-center gap-2">
            <span v-if="application.status == 2" class="px-4 py-2 text-sm font-medium bg-red-300 rounded-lg">Belum Memenuhi Syarat</span>
            <span v-else-if="application.status == 3" class="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg">Tidak Memenuhi Syarat</span>
            <span v-else-if="application.status == 4" class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg">Memenuhi Syarat</span>
            <span v-else-if="application.status == 6" class="px-4 py-2 text-sm font-medium text-white bg-red-800 rounded-lg">Tidak Memenuhi Kuota</span>
            <span v-else class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg">Disetujui</span>
            <span
              v-if="application.hasil_verifikasi && application.status != 4"
              class="px-3 py-2 text-sm font-semibold rounded-lg"
              :class="application.hasil_verifikasi === 'TIDAK MAMPU' ? 'bg-yellow-200 text-yellow-800' : 'bg-blue-200 text-blue-800'"
            >{{ application.hasil_verifikasi }}</span>
          </div>
        </div>
        <div v-if="can('ranking permohonan beasiswa')" class="flex flex-wrap items-center gap-2 mt-4">
          <a :href="`/verified-scholar-applications/${application.id}/download-files`" class="btn-indigo">
            <font-awesome-icon icon="fas fa-download" class="mr-1" /> Download Semua File
          </a>
          <template v-if="canWrite('verify permohonan beasiswa') && application.status == 4">
            <div class="border-l border-gray-400 h-8 mx-1"></div>
            <Button type="button" class="btn-warning" @click.prevent="unverifiedApplication">
              <span><font-awesome-icon icon="fas fa-close" class="mr-1" />Unverified</span>
            </Button>
            <div class="border-l border-gray-400 h-8 mx-1"></div>
            <select
              v-model="hasil_verifikasi"
              class="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">-- Hasil Verifikasi --</option>
              <option value="TIDAK MAMPU">TIDAK MAMPU</option>
              <option value="PRESTASI">PRESTASI</option>
            </select>
            <Button type="button" class="btn-green" @click.prevent="acceptApplication">
              <span><font-awesome-icon icon="fas fa-check" class="mr-1" />Terima</span>
            </Button>
            <div class="border-l border-gray-400 h-8 mx-1"></div>
            <Button type="button" class="btn-red" @click.prevent="rejectApplication">
              <span><font-awesome-icon icon="fas fa-ban" class="mr-1" />Tidak Memenuhi Kuota</span>
            </Button>
          </template>
        </div>
      </div>
      <div class="flex flex-col w-full md:flex-row">
        <dl class="flex flex-wrap w-full p-6 leading-10 lg:w-1/2">
          <dt class="w-full lg:w-3/12">Periode Beasiswa</dt>
          <dd class="w-full lg:w-9/12">: {{ application.periode }}</dd>
          <dt class="w-full lg:w-3/12">NIM Pemohon</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nim }}</dd>
          <dt class="w-full lg:w-3/12">Nama Pemohon</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nama_pemohon }}</dd>
          <dt class="w-full lg:w-3/12">Nama Universitas</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nama_universitas }}</dd>
          <dt class="w-full lg:w-3/12">Program Studi</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nama_prodi }}</dd>
          <dt class="w-full lg:w-3/12">Berprestasi</dt>
          <dd class="w-full lg:w-9/12">: {{ application.is_berprestasi ? 'Ya' : 'Tidak' }}</dd>
          <dt class="w-full lg:w-3/12">Pra-Sejahtera</dt>
          <dd class="w-full lg:w-9/12">: {{ application.is_prasejatera ? 'Ya' : 'Tidak' }}</dd>
          <dt class="w-full lg:w-3/12">Semester</dt>
          <dd class="w-full lg:w-9/12">: {{ application.semester }}</dd>
          <dt class="w-full lg:w-3/12">Nilai UKT</dt>
          <dd class="w-full lg:w-9/12">: Rp. {{ $filters.formatNumber(application.nilai_ukt) }}</dd>
          <dt v-if="application.hasil_verifikasi" class="w-full lg:w-3/12">Hasil Verifikasi</dt>
          <dd v-if="application.hasil_verifikasi" class="w-full lg:w-9/12">
            : <span
              class="px-2 py-1 text-sm font-semibold rounded-lg"
              :class="application.hasil_verifikasi === 'TIDAK MAMPU' ? 'bg-yellow-200 text-yellow-800' : 'bg-blue-200 text-blue-800'"
            >{{ application.hasil_verifikasi }}</span>
          </dd>
        </dl>
        <div class="flex flex-wrap w-full p-6 leading-10 lg:w-1/2">
          <dt class="w-full lg:w-4/12">Jenis Kelamin</dt>
          <dd class="w-full lg:w-8/12">: {{ application.jenis_kelamin }}</dd>
          <dt class="w-full lg:w-4/12">Agama</dt>
          <dd class="w-full lg:w-8/12">: {{ application.agama }}</dd>
          <dt class="w-full lg:w-4/12">Alamat</dt>
          <dd class="w-full lg:w-8/12">: {{ application.alamat }}</dd>
          <dt class="w-full lg:w-4/12">Kec./Kel.</dt>
          <dd class="w-full lg:w-8/12">: Kecamatan {{ application.kecamatan }} Kelurahan {{ application.kelurahan }}</dd>
          <dt class="w-full lg:w-4/12">RT/RW</dt>
          <dd class="w-full lg:w-8/12">: RT {{ application.rt }} RW {{ application.rw }}</dd>
          <dt class="w-full lg:w-4/12">Telepon</dt>
          <dd class="w-full lg:w-8/12">: {{ application.telp }}</dd>
          <dt class="w-full lg:w-4/12">Orang Tua</dt>
          <dd class="w-full lg:w-8/12">: {{ application.nama_ayah }} / {{ application.nama_ibu }}</dd>
          <dt class="w-full lg:w-4/12">Penghasilan Orang Tua</dt>
          <dd class="w-full lg:w-8/12">: Rp. {{ $filters.formatNumber(application.penghasilan_orangtua) }}</dd>
          <dt class="w-full lg:w-4/12">Jumlah Tanggungan Orang Tua</dt>
          <dd class="w-full lg:w-8/12">: {{ application.jumlah_tanggungan_orangtua }} Orang</dd>
        </div>
      </div>
      <div
        v-if="application.catatan_verifikasi && (application.status == 2 || application.status == 3)"
        class="w-full p-8 text-base text-center border-t border-gray-200"
      >
        <h2>Catatan Verifikasi: </h2>
        {{ application.catatan_verifikasi }}
        <p class="mt-2">
          Diverifikasi Oleh: {{ application.verifikator }}
        </p>
      </div>
    </div>
    <div class="w-full bg-white rounded-md shadow">
      <div class="flex w-full px-8 py-6 bg-gray-200">
        <h2 class="text-xl">File Persyaratan</h2>
      </div>
      <div class="p-6">
        <ul class="mt-2 ml-4 list-disc">
          <li
            v-for="file in application.files" :key="`${file.id}-list`"
            :class="{ 'form-warning': file.status == 1, 'form-valid': file.status == 4, 'form-error': file.status == 2 || file.status == 3 }"
          >
            {{ file.persyaratan }} <font-awesome-icon
              v-if="file.status == 4" icon="fas fa-check"
              class="text-green-700"
            />
            <span v-if="file.status == 1" class="ml-1 form-warning">(Belum Verifikasi)</span>
          </li>
        </ul>
      </div>
      <table class="w-full">
        <thead>
          <tr class="font-bold text-left">
            <th class="px-6 pt-6 pb-4">Jenis File</th>
            <th class="px-6 pt-6 pb-4 text-center">Status Verifikasi</th>
            <th v-if="can('write permohonan beasiswa')" class="px-6 pt-6 pb-4 border-b">Aksi</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="file in application.files" :key="file.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
            <td class="p-4 border-t w-">
              {{ file.persyaratan }}
            </td>
            <td class="p-4 text-center border-t">
              <div>
                <span v-if="file.status == 2" class="px-4 py-2 text-sm bg-red-300 rounded-lg">Belum Memenuhi Syarat</span>
                <span v-else-if="file.status == 3" class="px-4 py-2 text-sm bg-red-500 rounded-lg">Tidak Memenuhi
                  Syarat</span>
                <span v-else class="px-4 py-2 text-sm bg-green-500 rounded-lg">Disetujui</span>
              </div>
              <p class="mt-2 leading-4 break-normal">{{ file.catatan_verifikasi }}</p>
            </td>
            <td class="w-px p-4 border-t">
              <Button
                type="button" title="Lihat File" class="rounded btn-green-sm"
                @click.prevent="viewImage(file.file_path)"
              >
                <font-awesome-icon icon="fas fa-magnifying-glass" />
              </Button>
            </td>
          </tr>
          <tr v-if="application.files.length === 0">
            <td class="px-6 py-4 border-t" colspan="3">Tidak ada data file persayaratan yang diupload.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="w-full mt-8 bg-white rounded-md shadow">
      <div class="w-full px-8 py-6 bg-gray-200">
        <h2 class="text-xl">History Permohonan Beasiswa</h2>
      </div>
      <table class="w-full">
        <thead>
          <tr class="font-bold text-left">
            <th class="w-3/12 px-6 pt-6 pb-4">Waktu</th>
            <th class="w-3/12 px-6 pt-6 pb-4 text-center">Jenis Aksi</th>
            <th class="w-6/12 px-6 pt-6 pb-4 border-b">Catatan</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="history in application.histories" :key="history.id"
            class="hover:bg-gray-100 focus-within:bg-gray-100"
          >
            <td class="p-4 border-t">
              {{ history.waktu }}
            </td>
            <td class="p-4 text-center border-t">
              {{ history.action }}
            </td>
            <td class="w-px p-4 border-t">
              {{ history.catatan }}
            </td>
          </tr>
          <tr v-if="application.histories.length === 0">
            <td class="px-6 py-4 border-t" colspan="3">Tidak ada data history.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <image-viewer :src="imageSource" :show-modal="showImage" @close="showImage = false" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import ImageViewer from '../../Shared/ImageViewer.vue'

export default {
  components: {
    Head,
    Link,
    ImageViewer,
  },
  layout: Layout,
  props: {
    application: Object,
  },
  remember: 'form',
  data() {
    return {
      imageSource: '',
      showImage: false,
      hasil_verifikasi: this.application.hasil_verifikasi || '',
    }
  },
  methods: {
    acceptApplication() {
      if (!this.hasil_verifikasi) {
        this.$swal.fire({
          title: 'Pilih Hasil Verifikasi',
          text: 'Anda harus memilih hasil verifikasi (TIDAK MAMPU / PRESTASI) terlebih dahulu!',
          icon: 'warning',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'OK',
        })
        return
      }
      this.$swal.fire({
        title: 'Anda yakin setujui permohonan beasiswa ini?',
        text: `Hasil Verifikasi: ${this.hasil_verifikasi}`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
      }).then((result) => {
        if (result.isConfirmed) {
          this.$inertia.put(`/scholar-applications/${this.application.id}/accept`, {
            hasil_verifikasi: this.hasil_verifikasi,
          })
        }
      })
    },
    async rejectApplication() {
      const { value: catatan } = await this.$swal.fire({
        title: 'Anda yakin tolak permohonan beasiswa ini?',
        input: 'text',
        inputLabel: 'Masukan Alasan Penolakan',
        inputValue: 'Tidak Memenuhi Kuota',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
        inputValidator: (value) => {
          if (!value) {
            return 'Catatan verifikasi harus diisi!'
          }
        },
      })

      if (catatan) {
        this.$inertia.put(`/scholar-applications/${this.application.id}/hold`, {
          'catatan_verifikasi': catatan,
        })
      }
    },
    unverifiedApplication() {
      this.$swal.fire({
        title: 'Anda yakin unverified permohonan beasiswa ini?',
        text: 'Harap verifikasi ulang sebelum merubah status!',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
      }).then((result) => {
        if (result.isConfirmed) {
          this.$inertia.put(`/scholar-applications/${this.application.id}/unverified`)
        }
      })
    },
    viewImage(imgSource) {
      this.imageSource = imgSource
      this.showImage = true
    },
  },
}
</script>
